 
 
import { SearchResultItemModel } from "../models/SearchResultItemModel";  
import { ApiClient, CategoryModel, CountryModel, CurrencyModel, DashBoardSummaryModel, FileParameter, ItemTypeEnum, LanguageModel } from "../generated/api";
import CONFIG_KEYS from "../config";  
import { customFetch } from "../middlewares/custom-fetch";
import { requestUtility } from "../helper/RequestUtility";

// Categories rarely change and are fetched by every page (Footer, home, search,
// course-edit all call getAllCategories independently on every navigation, since
// each page mounts its own <Layout>/<Footer>). Cache them at module scope so the
// list is only ever fetched once per tab session instead of on every page load;
// the promise is memoized too, so callers that mount at the same time (e.g. the
// homepage and its own footer) share a single in-flight request.
let categoriesCache: CategoryModel[] | null = null;
let categoriesPromise: Promise<CategoryModel[]> | null = null;

export class CommonService {
  private api: ApiClient;

  constructor(navigate?: (url: string) => void) {
    this.api = new ApiClient(CONFIG_KEYS.baseUrl, {
      fetch: customFetch
    });
  }

  // ── Dashboard sidebar counts — one endpoint each so every badge loads on its own ──
  private async getDashCount(path: string): Promise<number> {
    const res = await customFetch(`${CONFIG_KEYS.baseUrl}/api/Common/dashboard/count/${path}`, { method: "GET" });
    const val = await res.json();
    return typeof val === "number" ? val : (Number(val) || 0);
  }
  public GetFavouritesCount(): Promise<number> { return this.getDashCount("favourites-count"); }
  public GetFollowingCount(): Promise<number> { return this.getDashCount("following-count"); }
  public GetOwnCoursesCount(): Promise<number> { return this.getDashCount("own-courses-count"); }
  public GetEnrolledLecturesCount(): Promise<number> { return this.getDashCount("enrolled-lectures-count"); }
  public GetTeachingLecturesCount(): Promise<number> { return this.getDashCount("teaching-lectures-count"); }
  public GetJoinRequestsCount(): Promise<number> { return this.getDashCount("join-requests-count"); }
  public GetPendingExamsCount(): Promise<number> { return this.getDashCount("pending-exams-count"); }


  public async uploadFilesFormData(
    files: FileParameter[],
    fileType: number,         // FileTypeEnum numeric value
    tag?: string
  ) {
    const url = `${CONFIG_KEYS.baseUrl}/api/Common/uploadFiles`;
    const fd = new FormData();
  
    // IMPORTANT: field name MUST be "files" for each file
    for (const f of files) {
      // f.data is Blob/File; f.fileName is string
      fd.append("files", f.data as Blob, f.fileName);
    }
  
    // ASP.NET can parse either the enum name or number; number is safest
    fd.append("fileType", String(fileType));
  
    if (tag) fd.append("tag", tag);
  
    const resp = await customFetch(url, {
      method: "POST",
      body: fd, // do NOT set Content-Type; the browser will set the boundary
    });
  
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`uploadFiles failed: ${resp.status} ${text}`);
    }
    return (await resp.json()) as Array<{ url: string }>;
  }
  
  public async getBlobUrl(blobName:string|null|undefined): Promise<string|null|undefined> { 
    if(!blobName) return blobName;
    if( blobName.startsWith("http://") || blobName.startsWith("https://")) return blobName;
    return await this.api.exportLink(blobName);
  }
  
  public async getAllCurrencies(): Promise<CurrencyModel[]> {
    return await this.api.currencies();
  }
  public async getAllCategories(): Promise<CategoryModel[]> {
    if (categoriesCache) return categoriesCache;
    if (!categoriesPromise) {
      categoriesPromise = this.api.categories()
        .then(data => { categoriesCache = data; return data; })
        .catch(err => { categoriesPromise = null; throw err; });
    }
    return categoriesPromise;
  }
  public async getAlllanguages(): Promise<LanguageModel[]> {
    return await this.api.languages();
  }
  public async getAllCountries(): Promise<CountryModel[]> {
    return await this.api.countries();
  }
 
  public async search(
    keyword?: string,
    maxcount?: number,
    types?: ItemTypeEnum[] | undefined
  ): Promise<SearchResultItemModel[]> {
    if((keyword?.length??0) <3) return [];

    keyword = keyword ?? encodeURIComponent(";");
    maxcount = maxcount??10;
    return await this.api.search(keyword,maxcount, types?? [ItemTypeEnum.NONE]); 
  }
  
  public async myCourses(): Promise<SearchResultItemModel[]> {
    const result =   await this.api.myCoursesList();
    return result.map(item => ({
      ...item, 
      course: item
    }));
  }

  public async ownCourses(): Promise<SearchResultItemModel[]> {
    const result =   await this.api.ownCoursesList();
    return result.map(item => ({
      ...item, 
      course: item
    }));
  }
  
  public async getMyCalendarLectures(start:Date | undefined, end:Date | undefined): Promise<SearchResultItemModel[]> { 
    const result = await this.api.getMyCalendarLectures(start,end );
    return result.map(item => ({
      ...item,
      tutor:undefined,
      course:undefined,
      lecture: item.lecture!,
    }));
  }

  public async getFavourites(): Promise<SearchResultItemModel[]> {
    const [favouriteCourses, favouriteLectures] = await Promise.all([
      this.api.favoriteCoursesList(),
      this.api.favoriteLectures()
    ]);
  
    const courseItems = favouriteCourses.map(item => ({ 
      course: item,
      lecture: undefined,
      tutor: undefined
    }));
  
    const lectureItems = favouriteLectures.map(item => ({ 
      lecture: item,
      course: undefined,
      tutor: undefined
    }));
  
    return [...courseItems, ...lectureItems]; 
  }
 
  public async getFollowing(): Promise<SearchResultItemModel[]> { 
    const result = await this.api.getFollowing();

    return result.map(item => ({
      ...item,
      tutor: item
    }));
  }

  public async getMyLectures(): Promise<SearchResultItemModel[]> { 
    const result = await this.api.myLectures();
    return result.map(item => ({
      ...item,
      tutor:undefined,
      course:undefined,
      lecture: item
    }));
  }
  public async getTutorLectures(): Promise<SearchResultItemModel[]> {
    const result =  await this.api.tutorLectures();
    return result.map(item => ({
      ...item,
      tutor:undefined,
      course:undefined,
      lecture: item
    }));
  }
  public async getSubscriptedLectures(): Promise<SearchResultItemModel[]> {
    const result = await this.api.subscriptedLectures();
    return result.map(item => ({
      ...item,
      tutor:undefined,
      course:undefined,
      lecture: undefined, 
      memberEnrollmentModel: item
    }));
  }

  public async dashBoardSummary(): Promise<DashBoardSummaryModel | undefined> {
    try {
      const response = await this.api.dashBoardSummary();
      return response;
    } catch (e) {
      requestUtility.handleError(e);
    }
    return undefined;
  }

  // Not part of the generated client (added ahead of the next swagger/nswag regen).
  // Anonymous endpoint. Failures default every flag to "on" so an API hiccup
  // never takes the whole site down or hides a feature that should be available.
  public async getFeatureFlags(): Promise<{ isLiveApp: boolean; enableAgora: boolean; paypalCommissionPercentage: number }> {
    try {
      const res = await customFetch(`${CONFIG_KEYS.baseUrl}/api/Common/site-status`);
      const data = await res.json();
      return {
        isLiveApp: data?.isLiveApp !== false,
        enableAgora: data?.enableAgora !== false,
        paypalCommissionPercentage: Number(data?.paypalCommissionPercentage) || 0,
      };
    } catch {
      return { isLiveApp: true, enableAgora: true, paypalCommissionPercentage: 0 };
    }
  }

  public async getIsLiveApp(): Promise<boolean> {
    return (await this.getFeatureFlags()).isLiveApp;
  }

  // Not part of the generated client (added ahead of the next swagger/nswag regen).
  public async submitContactForm(name: string, email: string, subject: string, message: string): Promise<void> {
    await customFetch(`${CONFIG_KEYS.baseUrl}/api/Common/contact`, {
      method: "POST",
      body: JSON.stringify({ name, email, subject, message }),
    });
  }

};